"use client"

import { useState, useEffect, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { toast } from "react-hot-toast"
import { Calendar, Clock, ArrowUp, ArrowDown, Download, BarChart3, TrendingUp, Activity } from "lucide-react"
import moment, { defaultDateRange, formatApiDate, APP_TIMEZONE } from "../utils/timezone"
import DateRangePicker from '../components/ui/date-range-picker'
import { currentBusinessDateKey } from "../utils/locationHours"
import axios from "axios"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts'

export default function StartingClosingTablesTime() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [groupedEvents, setGroupedEvents] = useState({});
  const [analyticsData, setAnalyticsData] = useState({
    dailyActivity: [],
    gameTypeDistribution: [],
    hourlyActivity: [],
    tableUtilization: []
  });
  const [showAnalytics, setShowAnalytics] = useState(false);

  const fetchTableEvents = useCallback(async () => {
    try {
      setLoading(true);
      
      const startDateStr = formatApiDate(dateRange.start);
      const fetchEndStr = moment.tz(dateRange.end, APP_TIMEZONE).add(1, "day").format("YYYY-MM-DD");
      
      let url = `https://analytics.elitepokerlounge.com/table-events-brownsville?startdate=${startDateStr}&enddate=${fetchEndStr}`;
      
      const response = await axios.get(url);
      
      const grouped = groupEventsByDate(response.data);
      const selectedStart = formatApiDate(dateRange.start);
      const selectedEnd = formatApiDate(dateRange.end);
      const filteredGrouped = Object.fromEntries(
        Object.entries(grouped).filter(([date]) => date >= selectedStart && date <= selectedEnd)
      );
      setGroupedEvents(filteredGrouped);
      
      // Generate analytics data
      const analytics = generateAnalyticsData(response.data, selectedStart, selectedEnd);
      setAnalyticsData(analytics);
    } catch (error) {
      console.error("Error fetching table events:", error);
      toast.error("Failed to load table events data");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchTableEvents();
  }, [fetchTableEvents]);

  const groupEventsByDate = (events) => {
    const grouped = {};
    
    events.forEach(event => {
      const date = currentBusinessDateKey(moment.tz(event.dateandtime, APP_TIMEZONE), "brownsville");
      
      if (!grouped[date]) {
        grouped[date] = [];
      }
      
      grouped[date].push(event);
    });
    
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => {
        return moment(a.dateandtime).diff(moment(b.dateandtime));
      });
    });
    
    return grouped;
  };

  const generateAnalyticsData = (events, selectedStart, selectedEnd) => {
    // Daily Activity Analysis
    const dailyActivity = {};
    const gameTypeDistribution = {};
    const hourlyActivity = Array.from({ length: 24 }, (_, i) => ({
      hour: i,
      opens: 0,
      closes: 0,
      label: moment().hour(i).format('hA')
    }));
    
    events.forEach(event => {
      const date = currentBusinessDateKey(moment.tz(event.dateandtime, APP_TIMEZONE), "brownsville");
      if (selectedStart && selectedEnd && (date < selectedStart || date > selectedEnd)) return;
      const hour = moment(event.dateandtime).hour();
      const gameType = event.game_name || 'Unknown';
      
      // Daily activity
      if (!dailyActivity[date]) {
        dailyActivity[date] = { date, opens: 0, closes: 0, total: 0 };
      }
      
      if (event.event === 'starts') {
        dailyActivity[date].opens += event.table_count || 1;
        hourlyActivity[hour].opens += event.table_count || 1;
      } else {
        dailyActivity[date].closes += event.table_count || 1;
        hourlyActivity[hour].closes += event.table_count || 1;
      }
      
      dailyActivity[date].total += event.table_count || 1;
      
      // Game type distribution
      if (!gameTypeDistribution[gameType]) {
        gameTypeDistribution[gameType] = { name: gameType, value: 0, opens: 0, closes: 0 };
      }
      gameTypeDistribution[gameType].value += event.table_count || 1;
      
      if (event.event === 'starts') {
        gameTypeDistribution[gameType].opens += event.table_count || 1;
      } else {
        gameTypeDistribution[gameType].closes += event.table_count || 1;
      }
    });

    // Calculate table utilization
    const tableUtilization = Object.values(dailyActivity).map(day => ({
      ...day,
      utilization: day.opens > 0 ? ((day.closes / day.opens) * 100).toFixed(1) : 0
    }));

    return {
      dailyActivity: Object.values(dailyActivity),
      gameTypeDistribution: Object.values(gameTypeDistribution),
      hourlyActivity: hourlyActivity.filter(h => h.opens > 0 || h.closes > 0),
      tableUtilization
    };
  };

  const formatDateRange = () => {
    return `${moment(dateRange.start).format('MMM D, YYYY')} - ${moment(dateRange.end).format('MMM D, YYYY')}`;
  };

  const handleDateRangeApply = (start, end) => {
    setDateRange({ start, end });
    setIsDatePickerOpen(false);
  };

  const getEventIcon = (event) => {
    if (event.event === "starts") {
      return <ArrowUp className="w-5 h-5 text-green-500" />;
    } else {
      return <ArrowDown className="w-5 h-5 text-red-500" />;
    }
  };

  const getEventColor = (event) => {
    return event.event === "starts" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200";
  };

  const downloadReport = () => {
    try {
      // Prepare data for CSV format
      const csvData = [];
      
      // Headers
      csvData.push(['Date', 'Time', 'Game Name', 'Event Type', 'Table Count']);
      
      // Data rows
      Object.keys(groupedEvents).sort().forEach(date => {
        groupedEvents[date].forEach(event => {
          csvData.push([
            moment(event.dateandtime).format('YYYY-MM-DD'),
            moment(event.dateandtime).format('HH:mm:ss'),
            event.game_name || 'Unknown',
            event.event === 'starts' ? 'Table Opened' : 'Table Closed',
            event.table_count || 1
          ]);
        });
      });
      
      // Convert to CSV string
      const csvContent = csvData.map(row => 
        row.map(field => 
          typeof field === 'string' && field.includes(',') 
            ? `"${field}"` 
            : field
        ).join(',')
      ).join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const startDateStr = formatApiDate(dateRange.start);
      const endDateStr = formatApiDate(dateRange.end);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `table-events-${startDateStr}-to-${endDateStr}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download report");
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  return (
    <div className="p-6 bg-gray-50">
      <div className="w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Table Opening & Closing Times</h1>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors duration-200"
            >
              <BarChart3 className="w-4 h-4" />
              {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
            </button>
            
            <button
              onClick={downloadReport}
              disabled={Object.keys(groupedEvents).length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Download CSV
            </button>
            
            <div 
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 px-3 py-1.5 rounded-md border border-gray-300 hover:border-blue-500 shadow-sm transition-colors duration-200 cursor-pointer"
              onClick={() => setIsDatePickerOpen(true)}
            >
              <Calendar className="w-4 h-4" />
              <span className="font-medium">{formatDateRange()}</span>
            </div>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && !loading && (
          <div className="mb-8 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Analytics Dashboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Daily Activity Chart */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Daily Table Activity</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analyticsData.dailyActivity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(date) => moment(date).format('MMM D')} />
                        <YAxis />
                        <Tooltip labelFormatter={(date) => moment(date).format('MMMM D, YYYY')} />
                        <Legend />
                        <Bar dataKey="opens" fill="#10B981" name="Tables Opened" />
                        <Bar dataKey="closes" fill="#EF4444" name="Tables Closed" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Game Type Distribution */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Game Type Distribution</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={analyticsData.gameTypeDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {analyticsData.gameTypeDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Hourly Activity Pattern */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Hourly Activity Pattern</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={analyticsData.hourlyActivity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="opens" stroke="#10B981" name="Opens" strokeWidth={2} />
                        <Line type="monotone" dataKey="closes" stroke="#EF4444" name="Closes" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Table Utilization */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Table Utilization Rate</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analyticsData.tableUtilization}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(date) => moment(date).format('MMM D')} />
                        <YAxis domain={[0, 100]} />
                        <Tooltip 
                          labelFormatter={(date) => moment(date).format('MMMM D, YYYY')}
                          formatter={(value) => [`${value}%`, 'Utilization Rate']}
                        />
                        <Bar dataKey="utilization" fill="#8B5CF6" name="Utilization %" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-600 text-sm font-medium">Total Opens</p>
                        <p className="text-2xl font-bold text-green-800">
                          {analyticsData.dailyActivity.reduce((sum, day) => sum + day.opens, 0)}
                        </p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-green-500" />
                    </div>
                  </div>
                  
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-red-600 text-sm font-medium">Total Closes</p>
                        <p className="text-2xl font-bold text-red-800">
                          {analyticsData.dailyActivity.reduce((sum, day) => sum + day.closes, 0)}
                        </p>
                      </div>
                      <ArrowDown className="w-8 h-8 text-red-500" />
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-600 text-sm font-medium">Avg Daily Activity</p>
                        <p className="text-2xl font-bold text-blue-800">
                          {analyticsData.dailyActivity.length > 0 ? 
                            Math.round(analyticsData.dailyActivity.reduce((sum, day) => sum + day.total, 0) / analyticsData.dailyActivity.length) : 0}
                        </p>
                      </div>
                      <Activity className="w-8 h-8 text-blue-500" />
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-600 text-sm font-medium">Game Types</p>
                        <p className="text-2xl font-bold text-purple-800">
                          {analyticsData.gameTypeDistribution.length}
                        </p>
                      </div>
                      <BarChart3 className="w-8 h-8 text-purple-500" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.keys(groupedEvents).length > 0 ? (
              Object.keys(groupedEvents).sort().map(date => (
                <Card key={date} className="overflow-hidden">
                  <CardHeader className="bg-gray-100">
                    <CardTitle className="text-xl">{moment(date).format('dddd, MMMM D, YYYY')}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {groupedEvents[date].map((event, index) => (
                        <div 
                          key={`${event.dateandtime}-${index}`} 
                          className={`flex items-center p-4 rounded-lg border ${getEventColor(event)}`}
                        >
                          <div className="mr-4">
                            {getEventIcon(event)}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{event.game_name}</h3>
                            <div className="flex items-center text-gray-600 mt-1">
                              <Clock className="w-4 h-4 mr-1" />
                              <span>{moment(event.dateandtime).format('h:mm A')}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="inline-block px-3 py-1 rounded-full bg-gray-200 text-gray-800 text-sm font-medium">
                              {event.event === "starts" ? "Table Opened" : "Table Closed"}
                            </span>
                            {event.table_count > 1 && (
                              <div className="mt-1 text-sm text-gray-600">
                                {event.table_count} tables
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <h3 className="text-lg font-medium text-gray-900">No table events found</h3>
                <p className="mt-2 text-gray-500">Try selecting a different date range</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Date Range Picker */}
      {isDatePickerOpen && (
        <DateRangePicker
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          onApply={handleDateRangeApply}
          initialStartDate={dateRange.start}
          initialEndDate={dateRange.end}
        />
      )}
    </div>
  );
}
